/**
 * Маршрути (TanStack Router, ADR-0002 розд. 5.4). Кожен маршрут — свій модуль у `routes/`
 * (E3a-01): тікети правлять модуль свого екрана, а тут лише збирається дерево. Новий екран —
 * новий файл у `routes/` і рядок у `routeTree`.
 *
 * Кореневий маршрут — оболонка і `/meta` (routes/root.tsx). Стартовий екран — в основному чанку;
 * решта екранів — ліниві чанки. Поки дані екрана не прийшли, лишається старий екран; після 300 мс —
 * скелетон (states.md, розд. 2). Помилка маршруту — лише в робочій області, оболонка лишається.
 * Попереднє завантаження за наміром (наведення) вмикає список сам, лише поза легким режимом
 * (lite-mode.md).
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRouter, type RouterHistory } from '@tanstack/react-router';
import { registerRouteSource } from '../support/diagnostics.ts';
import { createBfcacheHistory } from './history.ts';
import { trackArrivals } from './return-state.ts';
import { adminPageRoute, adminRoute } from './routes/admin.ts';
import { boardRoute } from './routes/board.ts';
import { homeRoute } from './routes/home.tsx';
import { loginRoute } from './routes/login.ts';
import { mailLinkRoute, mailRoute } from './routes/mail.ts';
import { duplicatesRoute, mergeRoute } from './routes/merge.ts';
import { legacyCardRoute, listRoute } from './routes/platform.ts';
import { profileRoute } from './routes/profile.ts';
import { createRecordRoute, recordRoute } from './routes/record.ts';
import { NotFound, RouteError, rootRoute } from './routes/root.tsx';
import { trashRoute } from './routes/trash.ts';

export type { RecordSearch, ResultMode } from './routes/record.ts';
export { LOGIN_PATH, type RouterContext } from './routes/root.tsx';

const routeTree = rootRoute.addChildren([
  homeRoute,
  recordRoute,
  createRecordRoute,
  loginRoute,
  listRoute,
  boardRoute,
  legacyCardRoute,
  adminRoute,
  adminPageRoute,
  profileRoute,
  trashRoute,
  duplicatesRoute,
  mergeRoute,
  mailRoute,
  mailLinkRoute,
]);

/**
 * `pendingMs` — поріг і мінімум показу скелетона маршруту; типово 300 мс (stability.md, правила 2–3).
 * Тест, що перевіряє сам скелетон очікування, передає 0: тоді він з'являється без справжнього часу.
 */
export function createAppRouter(
  queryClient: QueryClient,
  history?: RouterHistory,
  { pendingMs = 300 }: { pendingMs?: number } = {},
) {
  const router = createRouter({
    routeTree,
    // Без постійного `beforeunload`, інакше Firefox не кладе сторінку в bfcache (UX-34, history.ts).
    history: history ?? createBfcacheHistory(),
    context: { queryClient },
    defaultPendingMs: pendingMs,
    // Показаний скелетон маршруту — щонайменше 300 мс, як у <Area> (stability.md, правило 3).
    defaultPendingMinMs: pendingMs,
    defaultErrorComponent: RouteError,
    defaultNotFoundComponent: NotFound,
    // Дані маршрутів живуть у TanStack Query: свій кеш роутера не дублюємо.
    defaultPreloadStaleTime: 0,
    // Прокрутку й фокус при поверненні відновлює екран сам, коли його дані готові (UX-08,
    // app/return-state.ts): вбудоване відновлення роутера спрацьовує до даних списку.
    scrollRestoration: false,
  });
  trackArrivals(router.history);
  // Шаблон поточного маршруту (`/l/$entity/$id`, без значень) для технічних даних звернення.
  registerRouteSource(() => router.state.matches.at(-1)?.routeId ?? null);
  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
